// MapObject_ForTerrain.ts — the DB-free module holding the terrain-pipeline whitelist
// (`PackageUpMapObjForTerrain`) and its return type (`MapObject_ForTerrain`).
//
// The Set1 B3/B4 regression class lives HERE: every field the terrain pipeline reads from a
// MapObject must be forwarded explicitly, or it is silently dropped with no error.  Adding a
// field access to the pipeline WITHOUT updating this module raises a TypeScript error at the
// call-site (the return type is the whitelist).
//
// This module is deliberately DB-free: it imports NO mobx-graphlink / web-vcore / @apollo/client,
// so the Node test lane can import `PackageUpMapObjForTerrain` directly (see
// `MaterialRegions.test.ts`).  MapObject_ForTerrain_Resolver.ts re-exports from here for the
// app's runtime consumers.  Keeping the forwarder in its own module means a test never has to
// runtime-load `MapObject_ForTerrain_Resolver.js` (which drags in the whole DB/engine
// browser-runtime tree).
import {PathTerrainFeature} from "../../../IsolineTypes.js";
import {PathMaterialFeature} from "../../../../Materials/@MaterialFeature.js";
import {PathSceneryFeature} from "../../../../Scenery/@SceneryFeature.js";
import type {PathMeshSet} from "./RasterFeatures_Pure.js";
import type {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";

/** Returns only the fields the terrain height-gen pipeline consumes.
 *  Its return type is the whitelist; adding a field access to the pipeline
 *  without updating it here raises a TypeScript error at the call-site.
 *
 *  The param type is the STRUCTURAL subset of `MapObject` the forwarder reads (this module is
 *  DB-free — it cannot import the `MapObject` class without dragging mobx-graphlink into the
 *  Node lane).  A real `MapObject` is assignable (the fields are all compatible); hand-built
 *  test fixtures pass plain objects with just these fields. */
export function PackageUpMapObjForTerrain(
	obj: MapObject,
	extras: {
		resolvedWorldPos?: {x: number, y: number, z?: number},
		worldSeed?: number,
	},
	opts: {
		//calcPathPointsInWorld: boolean,
		resolvedPathOriginPathIDs?: Array<string>|n,
		resolvedPathMeshSets?: Array<PathMeshSet>|n,
		resolvedPathMeshSets_world?: Array<PathMeshSet>|n,
		resolvedPathClosedFlags?: Array<boolean>|n,
	}
) {
	return {
		// IMPORTANT: Any added data MUST be behind a field that starts with "_".

		// ADDED FIELD: _extras
		...(extras != null ? {_extras: extras} : {}),

		id: obj.id,
		...(obj.name != null && {name: obj.name as string|n}), // this is just included for convenience
		/** Used by TerrainRenderer for TerrainRegion AABB computation.
		 *  Returned as a plain {x,y} so fixture files don't need the Vec2 class. */
		//shape_size: obj.shape_size != null ? {x: obj.shape_size.x, y: obj.shape_size.y} : undefined,
		...(obj.shape_size != null && {shape_size: {x: obj.shape_size.x, y: obj.shape_size.y}}),
		/** Each modifier carries whichever of terrainDefinition / terrainRegion / terrainSpotHeight
		 *  is set; the other keys are absent (spread pattern) so fixture entries only need the ones
		 *  they use. */
		...(obj.modifiers != null && {modifiers: (obj.modifiers ?? []).filter(
			m=>m.terrainDefinition != null || m.terrainRegion != null || m.terrainSpotHeight != null,
		).map(m=>({
			enabled: m.enabled,
			...(m.terrainDefinition != null && {terrainDefinition: {
				...m.terrainDefinition.ExcludeKeys("heightColorGradients"),
				...(m.terrainDefinition.heightColorGradients != null && {heightColorGradients: m.terrainDefinition.heightColorGradients}),
			}}),
			...(m.terrainRegion != null && {terrainRegion: {terrainID: m.terrainRegion.terrainID}}),
			...(m.terrainSpotHeight != null && {terrainSpotHeight: {
				...(m.terrainSpotHeight.offset != null && {offset: m.terrainSpotHeight.offset}),
				elevation: m.terrainSpotHeight.elevation,
				radius: m.terrainSpotHeight.radius,
			}}),
		}))}),
		// Only paths with an "active" terrainFeature matter; `inheritPoints` + `transforms` are
		// forwarded verbatim (resolved downstream via `ResolvePathMeshes` → `ApplyPathTransformsToPoints`,
		// which runs the `interpolate` PathTransform — post-2026-08-01 home of `curveType`/`curve_*`). Curve shape lives in `_meshSet_world` (already curve-resolved); Strip* = pure projection.
		// Set12: paths with an ACTIVE `materialFeature` are forwarded too (the material-region
		// bridge `collectMaterialRegions` reads the same `_meshSet_world` / `_curve_closed_resolved`
		// fields the terrain-feature path does — the whitelist is shared).
		// Set13: paths with an ACTIVE `sceneryFeature` are forwarded too (the scenery-region bridge
		// `collectSceneryRegions` reads the same geometry — one shared whitelist).
		...(obj.paths != null && {paths: (obj.paths ?? []).map((p, pathIndex)=>{
			const tfActive = PathTerrainFeature.IsActive(p.terrainFeature);
			const mfActive = PathMaterialFeature.IsActive(p.materialFeature);
			const sfActive = PathSceneryFeature.IsActive(p.sceneryFeature);
			if (!tfActive && !mfActive && !sfActive) return null;
			const _originPathID = opts.resolvedPathOriginPathIDs?.[pathIndex];
			const _meshSet = opts.resolvedPathMeshSets?.[pathIndex];
			const _meshSet_world = opts.resolvedPathMeshSets_world?.[pathIndex];
			// The AUTHORITY closed flag for the tessellated mesh: the origin path's `curve_closed`,
			// possibly overridden by inheritance (ResolveInheritedBase threads the origin's flag up
			// the chain). `collectRasterFeatures` reads `_curve_closed_resolved`, NOT the raw flag.
			const _curve_closed_resolved = opts.resolvedPathClosedFlags?.[pathIndex] ?? p.curve_closed; // maybe temp; fallback to path's own curve_closed value if resolved-state is not provided by caller
			return {
				path_id: p.path_id,
				...(p.inheritPoints != null && {inheritPoints: p.inheritPoints}),
				...(p.transforms != null && {transforms: p.transforms}),
				...(p.terrainFeature != null && {terrainFeature: {
					...p.terrainFeature.ExcludeKeys("pinType"),
					...(p.terrainFeature.pinType != null && {pinType: p.terrainFeature.pinType}),
				}}),
				// Set12 — the material feature is forwarded VERBATIM (a new field on
				// `PathMaterialFeature` needs no whitelist edit — the Set1 B3/B4 anti-drift rule,
				// plan §3.3: MaterialRegion wraps the feature rather than copying its fields).
				...(p.materialFeature != null && {materialFeature: {...p.materialFeature}}),
				// Set13 — the scenery feature is forwarded VERBATIM (same anti-drift rule).
				...(p.sceneryFeature != null && {sceneryFeature: {...p.sceneryFeature}}),

				// OMITTED FIELDS (probably non-exhaustive)
				// omit "points"; this forces consumers to choose "_meshSet" or "_meshSet_world"
				// omit "curve_closed"; this forces consumers to read "_curve_closed_resolved" (the resolved flag, authoritative through the inheritance chain)

				// ADDED FIELDS
				...(_originPathID && {_originPathID}),
				...(_meshSet && {_meshSet}),
				...(_meshSet_world && {_meshSet_world}),
				...(_curve_closed_resolved != null && {_curve_closed_resolved}),
			};
		}).filter(p=>p != null)}),
	};
}

/** The exact subset of MapObject fields the terrain height-gen pipeline reads.
 *  Pass this type (instead of MapObject) to any terrain-system helper so that
 *  TypeScript enforces the field whitelist at compile time. */
export type MapObject_ForTerrain = ReturnType<typeof PackageUpMapObjForTerrain>;
